import React from 'react';
import { useNavigate } from 'react-router-dom';
import BackHeader from '../../components/BackHeader';
import NavigationBar from '../../components/NavigationBar';
import { useAddress } from '../../contexts/AddressContext';
import useTranslation from '../../hooks/useTranslation';
import { showChannelTalk, trackChannelTalkEvent } from '../../services/ChannelService';
import { getCurrentLanguage } from '../../config/languageConfig';

const AddressMethodSelection: React.FC = () => {
  const navigate = useNavigate();
  const { setSelectedMethod } = useAddress();
  const { t } = useTranslation();

  // Screenshot 방법 선택 (아직 액션 없음)
  const handleSelectScreenshot = () => {
    // TODO: Screenshot 방법 구현
    console.log('Screenshot method selected');
    setSelectedMethod('screenshot');
    navigate('/address-screenshot');
  };

  // GPS 방법 선택
  const handleSelectGPS = () => {
    setSelectedMethod('gps');
    navigate('/address?method=gps&mode=edit');
  };

  // 수동 입력 방법 선택
  const handleSelectManual = () => {
    setSelectedMethod('manual');
    navigate('/address?method=manual&mode=edit');
  };

  // 채널톡 도움말 열기
  const handleOpenHelp = () => {
    trackChannelTalkEvent('address_method_help_requested', {
      page: 'method_selection'
    });
    
    if (window.ChannelIO) {
      try {
        window.ChannelIO('boot', {
          "pluginKey": "4de468b6-e6bf-4446-a6d0-fb3ccb1132a6",
          "language": getCurrentLanguage(),
          "hideDefaultLauncher": true,
          "customLauncherSelector": "#custom-channel-button" 
        });
        
        setTimeout(() => {
          showChannelTalk();
        }, 500);
      } catch (error) {
        console.error('Failed to initialize ChannelTalk on help request:', error);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header with back button */}
      <BackHeader title={t('address.method.title')} />
      
      <div className="flex-1 px-4 py-6 pb-48">
        {/* Method selection buttons */}
        <div className="space-y-4">
          {/* Screenshot Method Button */}
          <button
            onClick={handleSelectScreenshot}
            className="w-full bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:bg-gray-50 active:bg-gray-100 transition-all duration-150 text-left"
          >
            <div className="flex items-center">
              <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-gray-900 mb-1">
                  {t('address.method.screenshot.title')}
                </h3>
                <p className="text-sm text-gray-600">
                  {t('address.method.screenshot.subtitle')}
                </p>
              </div>
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </button>

          {/* GPS Method Button */}
          <button
            onClick={handleSelectGPS}
            className="w-full bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:bg-gray-50 active:bg-gray-100 transition-all duration-150 text-left"
          >
            <div className="flex items-center">
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-gray-900 mb-1">
                  {t('address.method.gps.title')}
                </h3>
                <p className="text-sm text-gray-600">
                  {t('address.method.gps.subtitle')}
                </p>
              </div>
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </button>

          {/* Manual Method Button */}
          <button
            onClick={handleSelectManual}
            className="w-full bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:bg-gray-50 active:bg-gray-100 transition-all duration-150 text-left"
          >
            <div className="flex items-center">
              <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mr-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-gray-900 mb-1">
                  {t('address.method.manual.title')}
                </h3>
                <p className="text-sm text-gray-600">
                  {t('address.method.manual.subtitle')}
                </p>
              </div>
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </button>
        </div>
        
        {/* Help link */}
        <div className="mt-8 flex justify-center">
          <button
            type="button"
            onClick={handleOpenHelp}
            className="text-gray-600 hover:text-gray-800 focus:outline-none"
          >
            {t('address.help')}
          </button>
        </div>
      </div>
      
      {/* Navigation Bar */}
      <NavigationBar />
    </div>
  );
};

export default AddressMethodSelection; 